import { type DashboardTrack, type Milestone } from "@/lib/api";
import { phaseLabel, prototypeDotClass, stageBadgeClass } from "@/lib/phase";
import { ragLabel } from "@/lib/rag";
import { cn } from "@/lib/utils";

import { daysUntil, formatLong, formatShort, parseLocalDate } from "./dates";
import { splitByKind } from "./inline";
import { MilestonesPanel } from "./milestones-panel";

interface JourneyGanttProps {
  tracks: DashboardTrack[];
  programMilestones: Milestone[];
  deadline: string;
  thisMonday: string;
  editMode: boolean;
}

const MS_PER_DAY = 86_400_000;

/** Epoch millis for a `YYYY-MM-DD` string (local midnight). */
function ms(value: string): number {
  return parseLocalDate(value).getTime();
}

export function JourneyGantt({
  tracks,
  programMilestones,
  deadline,
  thisMonday,
  editMode,
}: JourneyGanttProps) {
  // Only champion tracks get lanes — the program track lives on Weekly Pulse.
  const { championTracks } = splitByKind(tracks);

  const weeksRemaining = Math.max(
    0,
    Math.ceil((tracks[0]?.days_to_deadline ?? daysUntil(deadline)) / 7),
  );

  // Timeline range: from min(today, earliest milestone) to the latest of
  // (today, deadline, latest milestone) — so it auto-extends past Aug 24 for
  // any Sept/Nov/later milestone. A little padding keeps end pins off the edge.
  const anchorTimes = [
    ms(thisMonday),
    ms(deadline),
    ...programMilestones.map((m) => ms(m.week_start)),
  ];
  let rangeStart = Math.min(...anchorTimes);
  let rangeEnd = Math.max(...anchorTimes);
  if (rangeEnd <= rangeStart) {
    rangeEnd = rangeStart + 7 * MS_PER_DAY;
  }
  const pad = Math.max(3 * MS_PER_DAY, (rangeEnd - rangeStart) * 0.05);
  rangeStart -= pad;
  rangeEnd += pad;

  /** Proportional left offset (0–100%) of a date within the padded range. */
  const leftPct = (value: string): number =>
    ((ms(value) - rangeStart) / (rangeEnd - rangeStart)) * 100;

  const todayPct = leftPct(thisMonday);

  // Sort milestones by date for stable pin ordering.
  const milestones = [...programMilestones].sort((a, b) =>
    a.week_start.localeCompare(b.week_start),
  );

  const LABEL_COL = "w-44 shrink-0";

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">
        Board deadline <span className="text-foreground">{formatLong(deadline)}</span> · Today{" "}
        <span className="text-foreground">{formatLong(thisMonday)}</span> ·{" "}
        <span className="text-foreground">{weeksRemaining}</span>{" "}
        {weeksRemaining === 1 ? "week" : "weeks"} remaining
      </p>

      <MilestonesPanel programMilestones={programMilestones} editMode={editMode} />

      <div className="border-border overflow-x-auto rounded-md border">
        <div className="min-w-[760px] p-4">
          {/* Axis header: milestone pins + a distinct Today marker */}
          <div className="flex">
            <div className={LABEL_COL} />
            <div className="relative h-16 flex-1">
              {/* Program milestone pins */}
              {milestones.map((m, i) => (
                <div
                  key={i}
                  className="absolute top-4 bottom-0 w-px"
                  style={{ left: `${leftPct(m.week_start)}%` }}
                >
                  <span className="text-foreground absolute -top-4 left-1/2 max-w-28 -translate-x-1/2 truncate text-center text-[10px] leading-tight font-medium">
                    {m.label}
                  </span>
                  <span className="text-muted-foreground absolute top-0 left-1/2 -translate-x-1/2 text-[9px] whitespace-nowrap">
                    {formatShort(m.week_start)}
                  </span>
                </div>
              ))}

              {/* Today marker — just the pill in the header (no line here); the
                  across-track "today" gridline in the lanes carries the vertical.
                  No date label: the current date already shows in the top-right. */}
              <div className="absolute top-0 bottom-0 z-10 w-px" style={{ left: `${todayPct}%` }}>
                <span className="bg-primary text-primary-foreground absolute bottom-0 left-1/2 -translate-x-1/2 rounded-sm px-1.5 py-0.5 text-[9px] font-medium whitespace-nowrap uppercase">
                  Today
                </span>
              </div>
            </div>
          </div>

          {/* Team lanes: each plots its current status at Today vs the pins */}
          {championTracks.length === 0 ? (
            <p className="text-muted-foreground border-border mt-2 border-t pt-4 text-sm">
              No teams yet.
            </p>
          ) : (
            <div className="border-border mt-2 border-t">
              {championTracks.map((track) => {
                const rag = track.latest_rag ?? undefined;
                return (
                  <div key={track.id} className="border-border flex items-stretch border-b">
                    <div className={cn(LABEL_COL, "py-3 pr-3")}>
                      <span className="text-foreground block text-sm font-normal">
                        {track.business_unit ?? track.name}
                      </span>
                    </div>
                    <div className="relative min-h-[3rem] flex-1">
                      {/* Faint gridlines aligned with the header pins + Today */}
                      <div
                        className="bg-primary/30 absolute top-0 bottom-0 w-px"
                        style={{ left: `${todayPct}%` }}
                      />
                      {milestones.map((m, i) => (
                        <div
                          key={i}
                          className="border-border absolute top-0 bottom-0 w-px border-l border-dashed"
                          style={{ left: `${leftPct(m.week_start)}%` }}
                        />
                      ))}

                      {/* Current status marker, plotted at Today */}
                      <div
                        className="absolute top-1/2 flex -translate-y-1/2 items-center gap-1.5"
                        style={{ left: `${todayPct}%` }}
                      >
                        <span
                          className={cn(
                            "h-3 w-3 shrink-0 rounded-full ring-2 ring-white",
                            prototypeDotClass(rag),
                          )}
                          title={rag ? ragLabel(rag) : "No status"}
                          aria-label={rag ? ragLabel(rag) : "No status"}
                        />
                        <span className={cn("whitespace-nowrap", stageBadgeClass(track.stage))}>
                          {phaseLabel(track.stage)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
